import React, { useImperativeHandle, forwardRef, useState, useEffect, useCallback } from 'react';
import { useDataAuth } from '../../hooks/useDataAuth';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { useTranslation } from "react-i18next";
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import { getCountries, getStatesAndCities, getOffice } from '../../services/sharedControls.service';
import '../../styles/components/_forms.scss'

export const LocationForm = forwardRef(({ dto, identification, handleInputChange, handleDirectInputChange, isEdit}, ref) => {
    const fieldsRequired = ['receiver_country', 'receiver_state', 'receiver_city', 'receiver_address', 'receiver_email'];
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    const [errorInputs, handleErrorInput, , validForm] = useErrorInputs({});


    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));


    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [offices, setOffices] = useState([]);

    const setValues = (obj) => {
        if (obj.country === 'CO') {
            GetStatesAndCities('STATE', { id_country: 1 });
            GetStatesAndCities('CITY', { code: obj.state });
        }
    }


    const fetchOffice = useCallback(async (identification) => {
        try {
            const response = await getOffice(userSession.token, identification);
            if (response.data) {
                if (response.data.length === 1) {
                    setValues(response.data[0]);
                }
                else
                    setOffices(response.data);
            }
        } catch (err) {
            console.log(err.message);
        }
    }, [userSession.token,]);

    useEffect(() => {
        if (identification)
            fetchOffice(identification);
    }, [identification, fetchOffice]);

    const fetchData = useCallback(async () => {
        try {
            const response = await getCountries(userSession.token);
            if (response.data)
                setCountries(response.data);
        } catch (err) {
            console.log(err.message);
        }
    }, [userSession.token]);

    const GetStatesAndCities = useCallback(async (action, variables) => {
        try {
            const response = await getStatesAndCities(userSession.token, action, variables);
            if (response.data) {
                if (action === 'STATE')
                    setStates(response.data);
                else {
                    setCities(response.data);
                }
            }
        } catch (err) {
            console.log(err.message);
        }
    }, [userSession.token]);

    useEffect(() => {
        fetchData();
        GetStatesAndCities('STATE', { id_country: 1 });
    }, [fetchData, GetStatesAndCities]);

    useEffect(() => {
        if (dto.receiver_country === 'CO' && dto.receiver_state) {
            GetStatesAndCities('CITY', { code: dto.receiver_state });
        }
    }, [isEdit, dto.receiver_country, dto.receiver_state, GetStatesAndCities]);

    const handleChangeCountryState = (e, action) => {
        const { name, value } = e.target;
        if (action === 'STATE') {
            handleDirectInputChange(name, value);
            if (value === 'CO')
                GetStatesAndCities('STATE', { id_country: 1 });
        }
        if (action === 'CITY') {
            handleDirectInputChange(name, value);
            GetStatesAndCities('CITY', { code: value });
        }
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
                {offices.length > 1 &&
                    <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth required size="small"
                            error={Boolean(errorInputs.group?.isError)}
                        >
                            <InputLabel>{t('commonFields.location.office')}</InputLabel>
                            <Select label={t('commonFields.location.office')} name="group"
                                value={dto.group}
                                onChange={handleInputChange}
                            >
                                {offices.map((option) => <MenuItem key={option.id_supplier_office} value={option.id_supplier_office}>{option.type_office}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                }
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.receiver_country?.isError)}
                    >
                        <InputLabel>{t('commonFields.location.country')}</InputLabel>
                        <Select label={t('commonFields.location.country')} name="receiver_country"
                            value={dto.receiver_country}
                            onChange={(e) => handleChangeCountryState(e, 'STATE')}
                        >
                            {countries.map((option) => <MenuItem key={option.id_country} value={option.code}>{option.name}</MenuItem>)}
                        </Select>
                        {errorInputs.receiver_country?.isError && <FormHelperText>{errorInputs.receiver_country.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    {dto.receiver_country === 'CO' ?
                        <FormControl fullWidth required size="small"
                            error={Boolean(errorInputs.receiver_state?.isError)}
                        >
                            <InputLabel>{t('commonFields.location.state')}</InputLabel>
                            <Select label={t('commonFields.location.state')} name="receiver_state"
                                value={dto.receiver_state}
                                onChange={(e) => handleChangeCountryState(e, 'CITY')}
                            >
                                {states.map((option) => <MenuItem key={option.id_state} value={option.code}>{option.name}</MenuItem>)}
                            </Select>
                            {errorInputs.receiver_state?.isError && <FormHelperText>{errorInputs.receiver_state.messageError}</FormHelperText>}
                        </FormControl>
                        :
                        <TextField fullWidth required size="small" label={t('commonFields.location.state')} name="receiver_state"
                            value={dto.receiver_state}
                            onChange={handleInputChange}
                            error={Boolean(errorInputs.receiver_state?.isError)}
                            helperText={errorInputs.receiver_state?.messageError || ''}
                        />
                    }
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    {dto.receiver_country === 'CO' ?
                        <FormControl fullWidth required size="small"
                            error={Boolean(errorInputs.receiver_city?.isError)}
                        >
                            <InputLabel>{t('commonFields.location.city')}</InputLabel>
                            <Select label={t('commonFields.location.city')} name="receiver_city"
                                value={dto.receiver_city}
                                onChange={handleInputChange}
                            >
                                {cities.map((option) => <MenuItem key={option.code} value={option.code}>{option.name}</MenuItem>)}
                            </Select>
                            {errorInputs.receiver_city?.isError && <FormHelperText>{errorInputs.receiver_city.messageError}</FormHelperText>}
                        </FormControl>
                        :
                        <TextField fullWidth required size="small" label={t('commonFields.location.cityLocation')} name="receiver_city"
                            value={dto.receiver_city}
                            onChange={handleInputChange}
                            error={Boolean(errorInputs.receiver_city?.isError)}
                            helperText={errorInputs.receiver_city?.messageError || ''}
                        />
                    }
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth required size="small" label={t('commonFields.location.address')} name="receiver_address"
                        value={dto.receiver_address}
                        onChange={handleInputChange}
                        error={Boolean(errorInputs.receiver_address?.isError)}
                        helperText={errorInputs.receiver_address?.messageError || ''}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth required size="small" label={t('commonFields.email')} name="receiver_email"
                        value={dto.receiver_email}
                        onChange={handleInputChange}
                        onBlur={(e) => {
                            const { name, value } = e.target;
                            if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value === '') {
                                handleErrorInput(name, { isError: false, messageError: '' });
                            } else {
                                handleErrorInput(name, { isError: true, messageError: 'receiver_email inválido.' });
                            }
                        }}
                        error={Boolean(errorInputs.receiver_email?.isError)}
                        helperText={errorInputs.receiver_email?.messageError || ''}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth size="small" type='number' label={t('commonFields.location.postalCode')} name="receiver_postalCode" className='input-no-spinner'
                        value={dto.receiver_postalCode}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth size="small" type='number' label={t('commonFields.phone')} name="receiver_phone" className='input-no-spinner'
                        value={dto.receiver_phone}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField fullWidth size="small" type='number' label={t('commonFields.extension')} name="reciever_extension" className='input-no-spinner'
                            value={dto.reciever_extension}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    
                </Grid>
            </Grid>
        </Box >
    );
});
